import { RoutesProps, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { DashboardActions } from '@shopify-multiseller/graphql/partner/types';
import { GET_DASHBOARD_ACTIONS_QUERY } from '../views/Dashboard/queries';
import { Badge } from '../components/primitives';
import { useAuth } from '../hooks/useAuth';
import { useTokens } from '../hooks/useToken';
import { useSaveBar } from '../hooks/useSaveBar';
import useScrollToTop from '../hooks/useScrollToTop';
import { AppShell, type ShellUser, type ShellSaveBar, type ShellUserMenuItem } from '../components/shell';

interface AdminUIProps {
  children: React.ReactElement<React.PropsWithChildren<RoutesProps>>;
}

function toInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
}

export const AdminUI: React.FC<React.PropsWithChildren<AdminUIProps>> = ({ children }) => {
  const auth = useAuth();
  const tokens = useTokens();
  const navigate = useNavigate();
  const { current } = useSaveBar();

  useScrollToTop();

  const u = auth?.user;
  const displayName =
    u?.seller?.displayName || [u?.firstName, u?.lastName].filter(Boolean).join(' ') || 'Account';

  const user: ShellUser | undefined = u
    ? {
        name: displayName,
        email: u.seller?.handle ? `@${u.seller.handle}` : undefined,
        initials: toInitials(displayName) || undefined,
      }
    : undefined;

  // The user dropdown — same destinations the legacy UserProfileMenu offered
  // (it stopped being rendered when the shell replaced the old sidebar).
  // Settings is a destination in the bar now, so it isn't repeated here;
  // Selling Fees and Terms & Conditions used to sit here pointing at
  // /selling-fees and /terms-and-conditions, neither of which was a route.
  const userMenu: ShellUserMenuItem[][] = [
    [{ label: 'Logout', onSelect: () => void auth?.logout() }],
  ];

  // What is waiting on the seller, carried in the bar on every screen. The
  // dashboard says it in full; everywhere else this is the reminder that it is
  // still there. Errors are ignored on purpose — a count that fails to load
  // must not take a working page down with it.
  const { data: actionsData } = useQuery<{ dashboardActions: DashboardActions }>(
    GET_DASHBOARD_ACTIONS_QUERY,
    { fetchPolicy: 'cache-first', errorPolicy: 'ignore' },
  );
  const needsAttention =
    (actionsData?.dashboardActions?.ordersPendingApproval ?? 0) +
    (actionsData?.dashboardActions?.ordersPendingFulfillment ?? 0);

  const attention =
    needsAttention > 0 ? (
      <Link to="/orders/triage" data-testid="attention-pill" className="shrink-0">
        <Badge variant="warning" dot>
          {`${needsAttention} need attention`}
        </Badge>
      </Link>
    ) : undefined;

  const saveBar: ShellSaveBar | undefined = current?.show
    ? {
        message: current.message,
        saving: current.saveAction.loading,
        saveDisabled: current.saveAction.disabled,
        discardLoading: current.discardAction.loading,
        onSave: current.saveAction.onAction,
        onDiscard: current.discardAction.onAction,
      }
    : undefined;

  return (
    <AppShell
      brand={tokens.general.shopLogo.string()}
      user={user}
      userMenu={userMenu}
      saveBar={saveBar}
      attention={attention}
    >
      {children}
    </AppShell>
  );
};
